import React from 'react';
import { Box, TextField, MenuItem, Typography } from '@mui/material';
import InlineAlert from '../../../shared/components/InlineAlert';

import { THINKING_EFFORT_TIERS } from '../services/agentSpecService';
import type { AgentSpecFormData } from '../hooks/useAgentSpecForm';
import { modelOptions } from './modelOptions';
import type { AvailableModel } from '../../../types/backend';

interface AgentSpecEditorProps {
  formData: AgentSpecFormData;
  fieldErrors: Record<keyof AgentSpecFormData, string | null>;
  successMessage: string | null;
  models: AvailableModel[];
  modelsLoading: boolean;
  /** A retired agent's row is history and takes no edits (PS-UC-18 4d). */
  readOnly: boolean;
  onChange: <K extends keyof AgentSpecFormData>(field: K, value: AgentSpecFormData[K]) => void;
}

const AgentSpecEditor: React.FC<AgentSpecEditorProps> = ({
  formData,
  fieldErrors,
  successMessage,
  models,
  modelsLoading,
  readOnly,
  onChange,
}) => {
  const effortTiers: string[] =
    formData.thinking_effort && !(THINKING_EFFORT_TIERS as readonly string[]).includes(formData.thinking_effort)
      ? [formData.thinking_effort, ...THINKING_EFFORT_TIERS]
      : [...THINKING_EFFORT_TIERS];

  return (
    <Box sx={{ px: 3, py: 2, display: 'flex', flexDirection: 'column' }}>
      {successMessage && (
        <InlineAlert severity="success" sx={{ mb: 2 }}>{successMessage}</InlineAlert>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start' }}>
        <Box sx={{ minWidth: 280, flex: '2 1 360px' }}>
          <TextField
            select
            label="Model"
            value={formData.model_id}
            onChange={(e) => onChange('model_id', e.target.value)}
            error={!!fieldErrors.model_id}
            helperText={
              fieldErrors.model_id
              || (modelsLoading ? 'Reading what this environment can run...' : 'Models this environment cannot run are shown disabled')
            }
            disabled={readOnly}
            size="small"
            fullWidth
          >
            {/* PS-UC-07 2a: a stored model the listing does not carry stays an option, so a
                deploy skew never renders the agent's model blank. A listed model, callable or
                not, is already among the options below. */}
            {formData.model_id && !models.some((m) => m.model_id === formData.model_id) && (
              <MenuItem value={formData.model_id}>
                {formData.model_id}
                <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 1 }}>
                  not in this environment
                </Typography>
              </MenuItem>
            )}
            {modelOptions(models)}
          </TextField>
        </Box>

        <Box sx={{ minWidth: 160, flex: '1 1 160px' }}>
          <TextField
            label="Max tokens"
            value={formData.max_tokens}
            onChange={(e) => onChange('max_tokens', e.target.value)}
            error={!!fieldErrors.max_tokens}
            helperText={fieldErrors.max_tokens || 'Max output tokens'}
            type="number"
            disabled={readOnly}
            size="small"
            fullWidth
          />
        </Box>

        <Box sx={{ minWidth: 180, flex: '1 1 180px' }}>
          <TextField
            select
            label="Thinking effort"
            value={formData.thinking_effort}
            onChange={(e) => onChange('thinking_effort', e.target.value)}
            helperText="Adaptive-thinking tier (none for sub-agents)"
            disabled={readOnly}
            size="small"
            fullWidth
          >
            <MenuItem value=""><em>None</em></MenuItem>
            {effortTiers.map((tier) => (
              <MenuItem key={tier} value={tier}>{tier}</MenuItem>
            ))}
          </TextField>
        </Box>

        <Box sx={{ minWidth: 180, flex: '1 1 180px' }}>
          <TextField
            label="Context window size"
            value={formData.context_window_size}
            onChange={(e) => onChange('context_window_size', e.target.value)}
            error={!!fieldErrors.context_window_size}
            helperText={fieldErrors.context_window_size || 'Optional — tokens'}
            type="number"
            disabled={readOnly}
            size="small"
            fullWidth
          />
        </Box>
      </Box>

    </Box>
  );
};

export default AgentSpecEditor;
